import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconPlus, IconLoader, IconAlert, IconEdit, IconTrash, IconCheck, IconSearch } from "../components/Icons";

interface AresResult {
  ico: string;
  obchodni_jmeno: string;
  dic: string | null;
  address_street: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
}

interface Supplier {
  id: string;
  name: string;
  ico: string | null;
  dic: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  is_active: boolean;
}

export default function Suppliers() {
  const { user } = useCurrentUser();
  const canWrite = can(user, "suppliers_write");
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState<Partial<Supplier>>({ name: "", ico: "", dic: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "CZ", is_active: true });
  const [aresStatus, setAresStatus] = useState<"idle" | "loading" | "ok" | "notfound" | "error">("idle");
  const [aresMsg, setAresMsg] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<Supplier[]>("/suppliers");
      setSuppliers(data);
    } catch (e) {
      setErr(apiError(e, t("suppliers.loadError")));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function lookupAres() {
    const ico = (form.ico || "").trim();
    if (ico.length < 6) {
      setAresStatus("error");
      setAresMsg(t("suppliers.icoMinLength"));
      return;
    }
    setAresStatus("loading");
    setAresMsg(t("suppliers.aresSearching"));
    try {
      const { data } = await api.get<AresResult>(`/ares/lookup?ico=${encodeURIComponent(ico)}`);
      setForm((prev) => ({
        ...prev,
        name: data.obchodni_jmeno || prev.name,
        dic: data.dic || prev.dic,
        address: data.address_street || prev.address,
        city: data.city || prev.city,
        postal_code: data.postal_code || prev.postal_code,
        country: data.country || prev.country,
        ico: data.ico,
      }));
      setAresStatus("ok");
      setAresMsg(t("suppliers.aresFound", { name: data.obchodni_jmeno }));
    } catch (e: any) {
      const s = e?.response?.status;
      setAresStatus(s === 404 ? "notfound" : "error");
      setAresMsg(s === 404 ? t("suppliers.aresNotFound") : t("suppliers.aresError"));
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const data = { ...form };
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, data);
      } else {
        await api.post("/suppliers", data);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", ico: "", dic: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "CZ", is_active: true });
      await load();
    } catch (e) {
      setErr(apiError(e, t("suppliers.saveError")));
    }
  }

  async function remove(id: string) {
    if (!confirm(t("suppliers.deleteConfirm"))) return;
    try {
      await api.delete(`/suppliers/${id}`);
      await load();
    } catch (e) {
      setErr(apiError(e, t("suppliers.deleteError")));
    }
  }

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{t("suppliers.title")}</h1>
          <p>{t("suppliers.subtitle")}</p>
        </div>
        {canWrite && <button className="primary" onClick={() => { setEditing(null); setShowForm(true); }}><IconPlus size={18} /> {t("suppliers.newSupplier")}</button>}
      </div>

      {err && <div className="card" style={{ padding: 12, marginBottom: 20, background: "var(--red-dim)", color: "var(--red)" }}><IconAlert size={16} /> {err}</div>}

      {canWrite && showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>{editing ? t("suppliers.editSupplier") : t("suppliers.newSupplier")}</h3>
          <form onSubmit={save} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            <div><label>{t("suppliers.name")} *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
            <div>
              <label>{t("suppliers.ico")}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={form.ico || ""}
                  onChange={e => setForm({ ...form, ico: e.target.value })}
                  onBlur={lookupAres}
                  placeholder={t("suppliers.icoPlaceholder")}
                  style={{ flex: 1 }}
                />
                <button type="button" onClick={lookupAres} disabled={aresStatus === "loading"} style={{ flexShrink: 0 }}>
                  {aresStatus === "loading" ? <IconLoader size={18} /> : <><IconSearch size={18} /> ARES</>}
                </button>
              </div>
              <p style={{ color: "var(--muted)", fontSize: 12, margin: "6px 0 0" }}>{t("suppliers.icoHint")}</p>
              {aresMsg && (
                <div style={{
                  marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                  color: aresStatus === "ok" ? "var(--green)" : aresStatus === "notfound" ? "var(--yellow)" : "var(--red)",
                }}>
                  {aresStatus === "ok" && <IconCheck size={14} />}
                  {aresMsg}
                </div>
              )}
            </div>
            <div><label>{t("suppliers.dic")}</label><input value={form.dic || ""} onChange={e => setForm({ ...form, dic: e.target.value })} /></div>
            <div><label>{t("suppliers.email")}</label><input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><label>{t("suppliers.phone")}</label><input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label>{t("suppliers.address")}</label><input value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div><label>{t("suppliers.city")}</label><input value={form.city || ""} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div><label>{t("suppliers.postalCode")}</label><input value={form.postal_code || ""} onChange={e => setForm({ ...form, postal_code: e.target.value })} /></div>
            <div><label>{t("suppliers.country")}</label><input value={form.country || "CZ"} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> {t("suppliers.active")}
              </label>
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
              <button className="primary" type="submit">{editing ? t("common.save") : t("common.create")}</button>
              <button className="ghost" type="button" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
            </div>
          </form>
        </div>
      )}

      {suppliers.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--muted)" }}>{t("suppliers.noSuppliers")}</div>
      ) : (
        <div className="card" style={{ overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>{t("suppliers.name")}</th>
                <th>{t("suppliers.ico")}</th>
                <th>{t("suppliers.dic")}</th>
                <th>{t("suppliers.contact")}</th>
                <th>{t("suppliers.address")}</th>
                <th>{t("common.status")}</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.name}</td>
                  <td className="mono">{s.ico || "—"}</td>
                  <td className="mono">{s.dic || "—"}</td>
                  <td>{s.email || s.phone || "—"}</td>
                  <td>{[s.address, s.city, s.postal_code].filter(Boolean).join(", ") || "—"}</td>
                  <td><span className={s.is_active ? "badge badge-green" : "badge badge-red"}>{s.is_active ? t("common.active") : t("common.inactive")}</span></td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      {canWrite && (
                      <>
                      <button className="ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => { setEditing(s); setForm(s); setShowForm(true); }}><IconEdit size={14} /> {t("common.edit")}</button>
                      <button className="ghost" style={{ padding: "4px 10px", fontSize: 12, color: "var(--red)" }} onClick={() => remove(s.id)}><IconTrash size={14} /> {t("common.delete")}</button>
                      </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
